import type { Timecard } from '../types';
import {
  filterTimecardsByCommonFilters,
  filterTimecardsByWeekRange,
  formatCommonFiltersSummary,
  formatTimecardReportStatusLabel,
  type TimecardReportCommonFilters,
  type TimecardReportStatusFilter,
} from './timecardReportFilters';
import { formatWeekRangeLabel } from './timecardReportFilters';
import { formatCompanyDate } from './companyDateFormat';

export type TimecardAnalysisStatusFilter = TimecardReportStatusFilter;
export { formatWeekRangeLabel };
export const formatTimecardAnalysisStatusLabel = formatTimecardReportStatusLabel;

export interface TimecardAnalysisFilterParams extends TimecardReportCommonFilters {
  fromWeekStartDate: string;
  toWeekStartDate: string;
}

export function filterTimecardsForAnalysis(
  timecards: Timecard[],
  params: TimecardAnalysisFilterParams
): Timecard[] {
  const common = filterTimecardsByCommonFilters(timecards, params);
  return filterTimecardsByWeekRange(common, params.fromWeekStartDate, params.toWeekStartDate);
}

export interface TimecardAnalysisLookupMaps {
  scheduleNameById?: Record<number, string>;
  positionNameById?: Record<number, string>;
}

export function buildTimecardAnalysisLookupMaps(
  schedules: Array<{ id: number; name: string }>,
  positions: Array<{ id: number; title: string }>
): TimecardAnalysisLookupMaps {
  const scheduleNameById: Record<number, string> = {};
  schedules.forEach((s) => {
    scheduleNameById[s.id] = s.name;
  });
  const positionNameById: Record<number, string> = {};
  positions.forEach((p) => {
    positionNameById[p.id] = p.title;
  });
  return { scheduleNameById, positionNameById };
}

export function resolveScheduleName(
  day: { scheduleId?: number | null; scheduleCode?: string | null },
  maps?: TimecardAnalysisLookupMaps
): string {
  if (day.scheduleId != null && maps?.scheduleNameById?.[day.scheduleId]) {
    return maps.scheduleNameById[day.scheduleId];
  }
  return day.scheduleCode || '';
}

export function resolvePositionName(
  day: { positionId?: number | null; positionCode?: string | null },
  maps?: TimecardAnalysisLookupMaps
): string {
  if (day.positionId != null && maps?.positionNameById?.[day.positionId]) {
    return maps.positionNameById[day.positionId];
  }
  return day.positionCode || '';
}

export function generateTimecardAnalysisData(
  timecards: Timecard[],
  lookupMaps?: TimecardAnalysisLookupMaps
) {
  const headers = [
    'Employee Name',
    'Date',
    'Scheduled In',
    'Scheduled Out',
    'Actual In',
    'Actual Out',
    'Paid In',
    'Paid Out',
    'Scheduled Hours',
    'Actual Hours',
    'Paid Hours',
    'Break Minutes',
    'Schedule',
    'Position',
    'Status',
  ];

  const rows: string[][] = [];

  timecards.forEach((timecard) => {
    const employeeName = timecard.employee
      ? `${timecard.employee.firstName} ${timecard.employee.lastName}`
      : 'Unknown';

    timecard.days.forEach((day) => {
      const formatTime = (dateTime: string | null | undefined) => {
        if (!dateTime) return '';
        try {
          const date = new Date(dateTime);
          return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
        } catch {
          return dateTime;
        }
      };

      const formatDate = (dateStr: string) => {
        try {
          const date = new Date(dateStr);
          return formatCompanyDate(date);
        } catch {
          return dateStr;
        }
      };

      const calculateHours = (inTime: string | null | undefined, outTime: string | null | undefined) => {
        if (!inTime || !outTime) return '0.00';
        try {
          const inDate = new Date(inTime);
          const outDate = new Date(outTime);
          const hours = (outDate.getTime() - inDate.getTime()) / (1000 * 60 * 60);
          return hours > 0 ? hours.toFixed(2) : '0.00';
        } catch {
          return '0.00';
        }
      };

      const scheduledHours = calculateHours(day.scheduledIn, day.scheduledOut);
      const actualHours = calculateHours(day.actualIn, day.actualOut);
      const paidHours = calculateHours(day.paidIn, day.paidOut);

      rows.push([
        employeeName,
        formatDate(day.date),
        formatTime(day.scheduledIn),
        formatTime(day.scheduledOut),
        formatTime(day.actualIn),
        formatTime(day.actualOut),
        formatTime(day.paidIn),
        formatTime(day.paidOut),
        scheduledHours,
        actualHours,
        paidHours,
        (day.paidBreakMinutes || day.actualBreakMinutes || day.scheduledBreakMinutes || 0).toString(),
        resolveScheduleName(day, lookupMaps),
        resolvePositionName(day, lookupMaps),
        timecard.isApproved ? 'Approved' : 'UnApproved',
      ]);
    });
  });

  return { headers, rows };
}

export function formatAnalysisFilterSummary(parameters: TimecardAnalysisFilterParams): string {
  const parts: string[] = [];

  if (parameters.fromWeekStartDate && parameters.toWeekStartDate) {
    parts.push(
      `${formatWeekRangeLabel(parameters.fromWeekStartDate)} → ${formatWeekRangeLabel(parameters.toWeekStartDate)}`
    );
  }

  const common = formatCommonFiltersSummary(parameters);
  if (common) parts.push(common);

  return parts.join(' · ') || 'All records';
}
